/**
 * Badge — a small, non-interactive status chip.
 *
 * Foregrounds are mixed a little toward `--text-primary` rather than used raw.
 * That is not decoration: `--warning` (#b45309) on `--warning-soft` lands at
 * 4.36:1 in the light theme, just under the bar. Mixing 12% of the theme's own
 * text colour into the hue darkens it in light mode and lightens it in dark
 * mode, taking every variant past 4.5:1 in both themes without a new token.
 */

import { forwardRef } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type BadgeVariant = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger';

/** Pull a hue toward the current theme's text colour to buy back contrast. */
export function mixText(token: string, weight = 88): string {
  return `color-mix(in srgb, ${token} ${weight}%, var(--text-primary))`;
}

/** A translucent wash of a hue — used for chip fills that must work on any surface. */
export function mixSurface(token: string, weight: number): string {
  return `color-mix(in srgb, ${token} ${weight}%, transparent)`;
}

const HUE: Record<BadgeVariant, { fg: string; bg: string }> = {
  neutral: { fg: 'var(--text-secondary)', bg: 'var(--surface-hover)' },
  accent: { fg: 'var(--accent-text)', bg: 'var(--accent-soft)' },
  info: { fg: 'var(--info)', bg: 'var(--info-soft)' },
  success: { fg: 'var(--success)', bg: 'var(--success-soft)' },
  warning: { fg: 'var(--warning)', bg: 'var(--warning-soft)' },
  danger: { fg: 'var(--danger)', bg: 'var(--danger-soft)' },
};

const SIZE: Record<'sm' | 'md', string> = {
  sm: 'h-4 gap-1 px-1 text-[length:var(--text-2xs)]',
  md: 'h-5 gap-1.5 px-1.5 text-xs',
};

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'color'> {
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  /** Leading dot in the variant's pure hue — for status, not decoration. */
  dot?: boolean;
  icon?: ReactNode;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { variant = 'neutral', size = 'md', dot = false, icon, className, children, style, ...rest },
  ref,
) {
  const hue = HUE[variant];

  return (
    <span
      ref={ref}
      // The hue this chip is encoding a value in, so the gold sweep can see it.
      // `--accent-soft` on `--accent-text` is invisible to a sweep that only
      // compares background fills, which is how a gold "Admin" pill shipped 382px
      // from the gold Invite button under a clean invariants run.
      data-badge={variant}
      className={cn(
        'inline-flex max-w-full shrink-0 items-center rounded-sm font-medium whitespace-nowrap',
        'leading-none [&_svg]:size-3 [&_svg]:shrink-0',
        SIZE[size],
        className,
      )}
      style={{ background: hue.bg, color: mixText(hue.fg), ...style }}
      {...rest}
    >
      {dot ? (
        <span
          aria-hidden="true"
          className="size-1.5 shrink-0 rounded-full"
          style={{ background: hue.fg }}
        />
      ) : null}
      {icon}
      <span className="min-w-0 truncate">{children}</span>
    </span>
  );
});
